'use client';

import { useMemo } from 'react';

import { CountdownRenderProps } from 'react-countdown';

import { cn } from '@/lib/utils/cn';

/**
 * Creates a countdown renderer function that displays time in "X d Y h Z m W s" format
 * with translated unit labels in muted color.
 *
 * @param getTranslation - Function to get translation for countdown units (e.g., tPools('countdown.days'))
 * @returns A countdown renderer function
 */
export function getCountdownRenderer(getTranslation: (key: string) => string) {
  return (props: CountdownRenderProps) => {
    const { days, hours, minutes, seconds, completed } = props;

    if (completed) {
      return null;
    }

    return (
      <span
        className={cn(
          'flex justify-center items-center font-mono font-bold text-xl gap-2'
        )}
      >
        {days}{' '}
        <span className="text-muted-foreground">
          {getTranslation('countdown.days')}
        </span>{' '}
        {hours}{' '}
        <span className="text-muted-foreground">
          {getTranslation('countdown.hours')}
        </span>{' '}
        {minutes}{' '}
        <span className="text-muted-foreground">
          {getTranslation('countdown.minutes')}
        </span>{' '}
        {seconds}{' '}
        <span className="text-muted-foreground">
          {getTranslation('countdown.seconds')}
        </span>
      </span>
    );
  };
}

/**
 * Hook version that memoizes the renderer based on the translation function
 * Use this in React components for better performance
 *
 * @param getTranslation - Function to get translation for countdown units
 * @returns A memoized countdown renderer function
 */
export function useCountdownRenderer(getTranslation: (key: string) => string) {
  return useMemo(() => getCountdownRenderer(getTranslation), [getTranslation]);
}
